import { FC, useContext, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import ServiceFilter from "./service-filters";
import PractitionerList from "lib/components/common/practitioner-list";
import usePatientBookings from "lib/hooks/usePatientBookings";
import ErrorBox from "lib/components/common/error-box";
import { getItemFromArray } from "lib/utils/array-utils";
import { BookingContext } from "lib/contexts/booking-context";
import { useNavigate } from "react-router-dom";
import { PAGE_ROUTES } from "lib/constants/page-routes";

interface PatientBookAppointmentProps { }

const PatientBookAppointment: FC<PatientBookAppointmentProps> = ({ }) => {
  const {
    error,
    loading,
    getFilterOptions,
    getFilterByClinic,
    filterOptions,
    getFilterByTherapy,
    getFilterByPractitioner,
    setFilterOptions,
    postFilterPractitionerList,
    filterPractitioners,
  } = usePatientBookings();

  const { updateBookingState } = useContext<BookingState>(BookingContext);
  const navigateTo = useNavigate();

  useEffect(() => {
    // Initialize the component with filter options
    getFilterOptions();
  }, []);

  // Call filterPractitioners after filterOptions are loaded
  useEffect(() => {
    if (filterOptions.clinics.length > 0 || filterOptions.therapies.length > 0) {
      filterPractitioners({});
    }
  }, [filterOptions.clinics.length, filterOptions.therapies.length]);



  // Show loading state while initializing
  if (loading && (!filterOptions.clinics.length && !filterOptions.therapies.length && !filterOptions.practitioners.length)) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading booking options...</p>
      </div>
    );
  }

  if (error) return <ErrorBox message={error} />;

  const handlePractitionerBooking = (
    practitionerDetails: PractitionerAnalyticsDetails
  ) => {
    updateBookingState({
      clinic: getItemFromArray(
        practitionerDetails.clinics,
        "id",
        filterOptions.selectedClinicId
      ),
      therapy: getItemFromArray(
        practitionerDetails.therapies,
        "id",
        filterOptions.selectedTherapyId
      ),
      practitioner: practitionerDetails.practitioner,
    });
    navigateTo(PAGE_ROUTES.PATIENT.ConfirmAppointment);
  };

  const handleBookAppointment = (practitionerId: string) => {
    getFilterByPractitioner(practitionerId, handlePractitionerBooking);
  };

  const handleClinicChange = (clinicId: string) => {
    if (clinicId === "all") {
      // Only clear the clinic filter and re-query while respecting other selections
      setFilterOptions({
        ...filterOptions,
        selectedClinicId: clinicId,
      });
      filterPractitioners({
        clinicId: undefined,
        therapyId:
          filterOptions.selectedTherapyId === "all"
            ? undefined
            : filterOptions.selectedTherapyId,
        practitionerId:
          filterOptions.selectedPractitionerId === "all"
            ? undefined
            : filterOptions.selectedPractitionerId,
      });
      return;
    }

    // Update the selected clinic ID
    setFilterOptions({
      ...filterOptions,
      selectedClinicId: clinicId,
    });

    // Call the new filter practitioners API
    filterPractitioners({
      clinicId: clinicId === "all" ? undefined : clinicId,
      therapyId: filterOptions.selectedTherapyId === "all" ? undefined : filterOptions.selectedTherapyId,
      practitionerId: filterOptions.selectedPractitionerId === "all" ? undefined : filterOptions.selectedPractitionerId,
    });
  };

  const handleTherapyChange = (therapyId: string) => {
    if (therapyId === "all") {
      // Only clear the therapy filter and re-query while respecting other selections
      setFilterOptions({
        ...filterOptions,
        selectedTherapyId: therapyId,
      });
      filterPractitioners({
        clinicId:
          filterOptions.selectedClinicId === "all"
            ? undefined
            : filterOptions.selectedClinicId,
        therapyId: undefined,
        practitionerId:
          filterOptions.selectedPractitionerId === "all"
            ? undefined
            : filterOptions.selectedPractitionerId,
      });
      return;
    }

    // Update the selected therapy ID
    setFilterOptions({
      ...filterOptions,
      selectedTherapyId: therapyId,
    });

    // Call the new filter practitioners API
    filterPractitioners({
      clinicId: filterOptions.selectedClinicId === "all" ? undefined : filterOptions.selectedClinicId,
      therapyId: therapyId === "all" ? undefined : therapyId,
      practitionerId: filterOptions.selectedPractitionerId === "all" ? undefined : filterOptions.selectedPractitionerId,
    });
  };

  const handlePractitionerChange = (practitionerId: string) => {
    if (practitionerId === "all") {
      // Only clear the practitioner filter and re-query while respecting other selections
      setFilterOptions({
        ...filterOptions,
        selectedPractitionerId: practitionerId,
      });
      filterPractitioners({
        clinicId:
          filterOptions.selectedClinicId === "all"
            ? undefined
            : filterOptions.selectedClinicId,
        therapyId:
          filterOptions.selectedTherapyId === "all"
            ? undefined
            : filterOptions.selectedTherapyId,
        practitionerId: undefined,
      });
      return;
    }

    // Update the selected practitioner ID
    setFilterOptions({
      ...filterOptions,
      selectedPractitionerId: practitionerId,
    });

    // Call the new filter practitioners API
    filterPractitioners({
      clinicId: filterOptions.selectedClinicId === "all" ? undefined : filterOptions.selectedClinicId,
      therapyId: filterOptions.selectedTherapyId === "all" ? undefined : filterOptions.selectedTherapyId,
      practitionerId: practitionerId === "all" ? undefined : practitionerId,
    });
  };

  const handleClearClicked = () => {
    setFilterOptions({
      clinics: [],
      practitioners: [],
      therapies: [],
      selectedClinicId: "all",
      selectedTherapyId: "all",
      selectedPractitionerId: "all",
    });
    getFilterOptions();
  };

  return (
    <Row >
      <Col md={4}>
        <ServiceFilter
          isLoading={loading}
          clinics={filterOptions.clinics || []}
          practitioners={filterOptions.practitioners || []}
          therapies={filterOptions.therapies || []}
          onClinicChange={handleClinicChange}
          onTherapyChange={handleTherapyChange}
          onPractitionerChange={handlePractitionerChange}
          selectedClinicId={filterOptions.selectedClinicId || "all"}
          selectedTherapyId={filterOptions.selectedTherapyId || "all"}
          selectedPractitionerId={filterOptions.selectedPractitionerId || "all"}
          onClearClicked={handleClearClicked}
        />
      </Col>
      <Col md={8} className="booking-list">
        {!filterOptions.practitioners || filterOptions.practitioners.length === 0 ? (
          <div className="text-center px-3 mt-4 mt-md-0">
            <h4 className="fs-5 fs-md-4 fs-lg-3">
              No practitioners available for the selected filters.
            </h4>
          </div>
        ) : (
          <PractitionerList
            selectedTherapyId={filterOptions.selectedTherapyId || "all"}
            selectedClinicId={filterOptions.selectedClinicId || "all"}
            isLoading={loading}
            practitionersDetails={filterOptions.practitioners || []}
            onBookAppointment={handleBookAppointment}
          />
        )}
      </Col>
    </Row>
  );
};

export default PatientBookAppointment;
